import { useContext, useEffect, useState } from "react";
import api from "../../../../services/api";
import { ContextRegisterProduct } from "../context";
import Notification from "../../../../components/toast";
import { useForm } from "react-hook-form";
import functions from "../../../../util/functions";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema } from "./schema";
import LibBoolean from "../../../../util/libBool";
import reqTipoProduto from "../../../../services/reqMultiTenant/reqTipoProduto";
import reqProduto from "../../../../services/reqMultiTenant/reqProduto";
import libFloat from "../../../../util/libFloat";
import { list } from "postcss";

export const IndexHook = () => {
	const {
		register,
		handleSubmit,
		setValue,
		setFocus,
		setError,
		reset,
		watch,
		control,
		formState: { errors, isSubmitting },
		trigger,
	} = useForm({
		mode: "onSubmit",
		//quando revalidar os campos
		// reValidateMode: "onChange",
		resolver: zodResolver(schema),
		defaultValues: {
			id_produto: 0,
			id_funcionario: 1,
			id_subgrupo: 1,
			id_grupo: 1,
			id_tipoproduto: 1,
			nome: "",
			ean: "",
			und: "UND",
			ativo: true,
			destaque: false,
			mais_pedidos: false,
			balanca: false,
			ativoapi: true,
			urlfoto: "",
			qtatual: "0",
			valor: "",
			custo: "0",
			lucro: "0",
			markup: "0",
			rendimento: "100",
			descricaoapi: "",
			prod_promocao: "N",
			qtcritica: "1",
			qtde: 1,
			referencia: "",
			descmax: "100",
			comissao: "0",
			fracao: "1",
			valorpromoapi: "0",
			data: functions.dataTimeNow(),
			largura: null,
			comprimento: null,
			altura: null,
			fragil: false,
			caixa_propria: false,
			pesobruto: null,
		},
	});

	const { handleCloseModalRegister, id_produto, setId_produto, setProductsData, productsData, isOpenModalRegister, selectedProduct, filterData } =
		useContext(ContextRegisterProduct);

	const [valueTab, setValueTab] = useState("0");

	const [valorInicial, setValorInicial] = useState(0);
	const [custoInicial, setCustoInicial] = useState(0);
	const [markupInicial, setMarkupInicial] = useState(0);
	const [valorPagoInicial, setValorPagoInicial] = useState(0);
	const [rendimentoInicial, setRendimentoInicial] = useState(0);
	const [loadingF, setLoadingF] = useState(false);

	const [categoryData, setCategoryData] = useState([]);

	const [isUpdatedImage, setIsUpdatedImage] = useState(false);
	const [loading, setLoading] = useState(false);

	const urlfoto = watch("urlfoto");

	//#region Fetch
	useEffect(() => {
		if (isOpenModalRegister) {
			fetchProduct();
			setValueTab("0");
		} else {
			reset();
		}
	}, [isOpenModalRegister]);

	const fetchProduct = async () => {
		try {
			setLoading(true);

			const [responseProduct, responseCategory] = await Promise.all([
				reqProduto.listID(id_produto),
				reqTipoProduto.listAll(),
			]);

			let productData = {};

			if (responseProduct.data && typeof responseProduct.data === "object") {
				productData = responseProduct.data;
			}
			setCategoryData(responseCategory.data);

			initializeProductValues(productData);

			processFields(productData);
		} catch (error) {
			Notification.showToast({
				message: error.message,
				type: "warning",
			});
			handleCloseModalRegister();
		} finally {
			setLoading(false);
		}
	};

	const fetchCategory = async () => {
		try {
			setLoading(true);
			const responseCategory = await reqTipoProduto.listAll();
			setCategoryData(responseCategory.data);
		} catch (error) {
			Notification.showToast({
				message: error.message,
				type: "warning",
			});
			handleCloseModalRegister();
		} finally {
			setLoading(false);
		}
	};

	const initializeProductValues = (product) => {
		setValorInicial(functions.parseNumber(product.valor));
		setCustoInicial(functions.parseNumber(product.lucro));
		setMarkupInicial(functions.parseNumber(product.markup));
		setValorPagoInicial(functions.parseNumber(product.custo));
		setRendimentoInicial(functions.parseNumber(product.rendimento));
		setIsUpdatedImage(false);
	};

	const processFields = (product) => {
		const floatFields = ["valor", "markup", "valorpago", "custo", "lucro", "valorpromoapi"];
		const numericFields = ["rendimento", "qtatual", "descmax", "comissao", "qtcritica", "fracao"];

		Object.keys(product).forEach((field) => {
			const value = product[field];

			if (field === "ativo") {
				setValue(field, value === "SIM");
			} else if (field === "ativoapi") {
				setValue(field, value === "SIM");
			} else if (field === "data") {
				setValue(field, functions.dateByString(value));
			} else if (floatFields.includes(field) && typeof value === "number") {
				setValue(field, value.toFixed(2).replace(".", ","));
			} else if (numericFields.includes(field) && typeof value === "number") {
				const stringValue = value.toString();

				setValue(field, stringValue.replace(".", ","));
			} else {
				setValue(field, value);
			}
		});
	};

	//#endregion

	//#region Calc Fields
	const handleFieldBlur = (e, fieldName) => {
		if (
			fieldName === "valor" || //
			fieldName === "markup" ||
			fieldName === "valorpago" ||
			fieldName === "custo" ||
			fieldName === "lucro" ||
			fieldName === "rendimento"
		) {
			const valor = functions.parseNumber(functions.removePoint(watch("valor")));

			if (valor === undefined || isNaN(valor)) {
				e.preventDefault();
				// O valor é undefined ou NaN
				return;
			}

			calcValues(fieldName);
		}
	};

	const calcValues = (fieldName) => {
		const tolerance = 0.01;

		let valor = functions.parseNumber(functions.removePoint(watch("valor")));
		let markup = functions.parseNumber(functions.removePoint(watch("markup")));
		let valorPago = functions.parseNumber(functions.removePoint(watch("custo")));
		let custo = functions.parseNumber(functions.removePoint(watch("lucro")));
		let rendimento = functions.parseNumber(functions.removePoint(watch("rendimento")));

		if (rendimento === 0) rendimento = 100;
		// if (custo === 0) rendimento = valorPago;
		if (custo === 0) custo = valorPago;

		if (
			Math.abs(valor - valorInicial) < tolerance &&
			Math.abs(custo - custoInicial) < tolerance &&
			Math.abs(markup - markupInicial) < tolerance &&
			Math.abs(valorPago - valorPagoInicial) < tolerance &&
			Math.abs(rendimento - rendimentoInicial) < tolerance
		)
			return;

		if (valorPago === 0 && custo > 0) valorPago = custo;

		if (fieldName === "valor") {
			if (valor > 0 && valorPago > 0) {
				custo = valorPago / (rendimento / 100);
				markup = ((valor - custo) / custo) * 100;
			}
		} else if (
			fieldName === "lucro" ||
			fieldName === "rendimento" ||
			fieldName === "valorpago" ||
			fieldName === "custo"
		) {
			if (valor > 0 && valorPago > 0) {
				custo = valorPago / (rendimento / 100);
				markup = ((valor - custo) / custo) * 100;
			}
		} else if (fieldName === "markup") {
			if (custo > 0 && markup > 0) {
				valor = custo + custo * (markup / 100);
			}
		}

		setValue(
			"valor",
			isNaN(valor)
				? "0"
				: valor.toLocaleString("pt-BR", {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					}),
		);
		setValue(
			"lucro",
			isNaN(custo)
				? "0"
				: custo.toLocaleString("pt-BR", {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					}),
		);
		setValue("rendimento", isNaN(rendimento) ? "0" : rendimento.toLocaleString("pt-BR"));
		setValue(
			"custo",
			isNaN(valorPago)
				? ""
				: valorPago.toLocaleString("pt-BR", {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					}),
		);
		setValue("markup", isNaN(markup) ? "" : markup.toLocaleString("pt-BR"));
	};
	//#endregion

	//#region onSubmit
	const handleOnSubmit = async (data, shouldCloseModal) => {
		try {
			setLoadingF(true);
			const newAtivo = LibBoolean.booleanToStr(data.ativo);
			const newAtivoApi = LibBoolean.booleanToStr(data.ativoapi);
			const newValor = libFloat.strBrToCurrency(data.valor);
			const newCusto = libFloat.strBrToCurrency(data.custo);
			const newLucro = libFloat.strBrToCurrency(data.lucro);
			const newMarkup = libFloat.strBrToCurrency(data.markup);
			const newValorPromoApi = libFloat.strBrToCurrency(data.valorpromoapi);
			const newQtAtual = libFloat.strBrToCurrency(data.qtatual);
			const newQtCritica = libFloat.strBrToCurrency(data.qtcritica);
			const newRendimento = libFloat.strBrToCurrency(data.rendimento);

			const newData = {
				...data,

				ativo: newAtivo,
				ativoapi: newAtivoApi,

				valor: newValor,
				custo: newCusto,
				lucro: newLucro,
				markup: newMarkup,
				valorpromoapi: newValorPromoApi,
				qtatual: newQtAtual,
				qtcritica: newQtCritica,
				rendimento: newRendimento,
			};

			const response = await reqProduto.insert(newData);

			const currentId = response.data[0].id_produto;
			setIsUpdatedImage(false);
			await reloadData(currentId);
			// Sucesso
			Notification.showToast({
				message: "Registro salvo com sucesso.",
				type: "success",
			});

			return true;
		} catch (error) {
			if (error.field) {
				errosAfterPost(error.field, error.message);
			} else
				Notification.showToast({
					message: error.message,
					type: "error",
				});

			shouldCloseModal = false;

			return false; // Sucesso
		} finally {
			setLoadingF(false);

			if (shouldCloseModal) handleCloseModalRegister();
		}
	};

	const reloadData = async (currentId) => {
		try {
			//lista o cliente no qual foi feita a inserção ou alteração
			const response = await reqProduto.listID(currentId);
			//pego somente o primeiro pq neste caso mando sempre 1 resgistro por vez
			const responseData = response.data;

			//se é inserção ou alteração. Inserção só adiciona mais um registro no array
			if (id_produto === 0) {
				setProductsData((prevState) => [...prevState, responseData]);
				const newIdProduto = responseData.id_produto;
				//se o cliente estiver mudando para a aba foto ele prepara o id
				setId_produto(newIdProduto);
				setValue("id_produto", newIdProduto);
			} else {
				//se for update ele procura na lista aonde ta o cliente que foi selecionado e altera somente ele
				const newProductsData = productsData.map((item) => {
					if (item.id_produto === responseData.id_produto) {
						return responseData;
					}
					return item;
				});
				setProductsData(newProductsData);
			}
		} catch (error) {
			console.log(error.message);
		}
	};

	const errosAfterPost = (field, message) => {
		setError(field?.toLowerCase(), { type: "manual", message: message });
		const newObjError = { [field]: message };

		handleOnError(newObjError);
	};

	//para chamar o submit sem ser pelo click no botão
	const executeSubmitForm = async () => {
		// Dispara a validação do formulário antes de enviar
		const result = await trigger();
		if (result) {
			// Chama `handleSubmit` e captura o resultado de `handleOnSubmit`
			const success = await new Promise((resolve) => {
				handleSubmit(
					async (data) => {
						const result = await handleOnSubmit(data);
						resolve(result); // Resolve com o resultado de `handleOnSubmit`
					},
					(errors) => {
						handleOnError(errors); // Lida com erros de validação
						resolve(false); // Resolve com `false` se houver erros
					},
				)();
			});

			return success; // Retorna o valor de sucesso/falha
		}
		return false; // Retorna `false` se a validação falhar
	};

	//#endregion

	//#region Mask and format Input
	const handleStrtoFloatChange = (event) => {
		const maskedFloat = functions.formatToFloat(event.target.value);
		// const maskedFloat = functions.formatarMoeda(event.target.value);
		setValue(event.target.name, maskedFloat);
	};

	const handleInputChange = (e) => {
		const { selectionStart, selectionEnd } = e.target; // Captura o cursor
		const upperCaseValue = e.target.value.toUpperCase();

		// Atualiza o valor no estado
		setValue(e.target.name, upperCaseValue);

		// Preserva o cursor após a atualização
		setTimeout(() => {
			e.target.setSelectionRange(selectionStart, selectionEnd);
		}, 0);

		// setValue(e.target.name, e.target.value.toUpperCase());
	};

	const handleCategoryChange = (e) => {
		findCategorySelect(e.target.value);
	};

	const findCategorySelect = (id) => {
		const selectedCategory = categoryData.find((category) => category.id_tipoproduto === parseInt(id, 10));

		setValue("categoria_nome", selectedCategory ? selectedCategory.nome : "");
		setValue("ordemcategoria", selectedCategory ? selectedCategory.ordem : "");
		// setValue(
		// 	"subitemapi",
		// 	selectedCategory ? selectedCategory.subitemapi : ""
		// );
	};

	const isPaiGrade = !!selectedProduct?.tem_grade;
	const isFilhoGrade = !!(selectedProduct?.id_produto_pai || filterData?.tipoproduto === "filho");

	const selectedCategoryId = watch("id_tipoproduto");

	useEffect(() => {
		if (selectedCategoryId) {
			findCategorySelect(selectedCategoryId);
		}
	}, [selectedCategoryId, categoryData, setValue]);

	//#endregion

	//#region tabs control

	//#region controla o foco quando muda de pagina
	useEffect(() => {
		const focusField = {
			0: "nome",
			1: "qtatual",
			2: "largura",
		};

		if (focusField[valueTab]) {
			setFocus(focusField[valueTab]);
		}
	}, [valueTab, setFocus]);
	//#endregion

	//#region controla os erros de cada tab
	const fieldsByTab = {
		//fields de cada tab para fazer validações
		0: ["nome", "ean", "und", "markup"],
		1: ["qtatual", "qtdecritica", "valor"],
		2: [],
		3: [],
		4: [],
	};

	const handleChangeTab = async (event, newValue) => {
		event.preventDefault();
		const currentTabFields = fieldsByTab[valueTab];

		const validFields = await trigger(currentTabFields); // Valida apenas os campos da aba atual

		if (validFields) {
			const shouldValidateNewTab = await validNewTab(newValue);

			if (shouldValidateNewTab) setValueTab(newValue);
		} else {
			Notification.showToast({
				message: "Preencha os campos obrigatórios antes de mudar de aba",
				type: "warning",
			});
		}
	};

	const validNewTab = async (newValue) => {
		if (newValue === "3" || newValue === "4") {
			//se for a tabela de fotos
			if (id_produto === 0) {
				//salva o produto para pegar o id_produto
				const shouldSubmit = await executeSubmitForm();
				return shouldSubmit;
			}
		}
		return true; // Indica que a nova aba pode ser ativada
	};

	const handleOnError = (errors) => {
		// no onSubmit={handleSubmit(handleOnSubmit, handleOnError )}
		// com isto encontrar erro ele vai chamar esta função
		// procura o primeiro erro e sua tab correspondente

		for (const tabIndex in fieldsByTab) {
			const fields = fieldsByTab[tabIndex];

			if (fields.some((field) => errors[field])) {
				setValueTab(tabIndex);
				break;
			}
		}
		Notification.showToast({
			message: "Preencha os campos obrigatórios antes de salvar",
			type: "warning",
		});
	};

	//#endregion

	//#endregion

	return {
		//hooks
		handleOnSubmit,
		handleFieldBlur,
		handleStrtoFloatChange,
		handleInputChange,
		handleChangeTab,
		handleCategoryChange,
		executeSubmitForm,
		fetchCategory,

		loading,
		loadingF,
		setLoadingF,
		urlfoto,
		id_produto,
		isUpdatedImage,
		setIsUpdatedImage,

		//context
		isOpenModalRegister,
		handleCloseModalRegister,

		//useForm
		register,
		handleSubmit,
		handleOnError,
		setValue,
		control,
		setFocus,

		//data
		categoryData,

		//formState
		errors,
		isSubmitting,

		//tab
		valueTab,

		//grade
		isPaiGrade,
		isFilhoGrade,
	};
};
